import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Wand2, Sparkles, Trash2, Image as ImageIcon, RotateCcw, BookOpen, Loader2 } from "lucide-react";

import { api } from "@/api/client";
import { useLang } from "@/context/LangContext";
import { COMICS, buildPagePrompt, deriveScenes } from "@/data/comics";
import { Button } from "@/components/ui/button";

const PageCell = ({ comic, page, index, illustration, lang, busy, onGenerate, onRegenerate }) => {
  const defaultPrompt = buildPagePrompt(comic, page, index);
  const [prompt, setPrompt] = useState(defaultPrompt);
  const [editing, setEditing] = useState(false);
  const scenes = Array.isArray(page.scenes) ? page.scenes : [];
  return (
    <div className="brutal p-3 bg-white flex flex-col" data-testid={`comic-illu-cell-${comic.slug}-${index}`}>
      <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">
        p{index + 1} · {page.layout || "panels"} {scenes.length ? `· ${scenes.length} escenas` : ""}
      </div>
      <div className="font-display font-bold text-sm truncate">
        {page.title?.[lang] || (index === 0 ? "Portada" : `Página ${index + 1}`)}
      </div>
      <div className="mt-2 aspect-square border-2 border-black rounded-lg overflow-hidden bg-canales-cream flex items-center justify-center relative">
        {illustration ? (
          <img
            src={`data:${illustration.mime_type};base64,${illustration.data}`}
            alt={`page ${index + 1}`}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="text-center p-2">
            <ImageIcon className="w-8 h-8 mx-auto text-neutral-400" />
            <p className="mt-1 text-xs font-body text-neutral-500">
              {lang === "es" ? "Sin generar" : "Not generated"}
            </p>
          </div>
        )}
        {busy && (
          <div className="absolute inset-0 bg-black/60 flex items-center justify-center text-white text-xs font-display font-black">
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            {lang === "es" ? "Generando…" : "Generating…"}
          </div>
        )}
      </div>

      {/* Editable prompt */}
      <div className="mt-2">
        {editing ? (
          <textarea
            data-testid={`comic-illu-prompt-${comic.slug}-${index}`}
            value={prompt}
            onChange={(e) => setPrompt(e.target.value)}
            rows={5}
            className="w-full brutal-sm bg-white p-2 text-[11px] font-body resize-y"
          />
        ) : (
          <div className="text-[10px] font-body text-neutral-600 line-clamp-3">{prompt}</div>
        )}
        <div className="mt-1 flex justify-between text-[10px]">
          <button
            onClick={() => setEditing((e) => !e)}
            data-testid={`comic-illu-edit-toggle-${comic.slug}-${index}`}
            className="underline font-display font-bold"
          >
            {editing ? (lang === "es" ? "Ocultar" : "Hide") : (lang === "es" ? "Editar prompt" : "Edit prompt")}
          </button>
          {editing && prompt !== defaultPrompt && (
            <button
              onClick={() => setPrompt(defaultPrompt)}
              className="underline text-canales-red"
            >
              {lang === "es" ? "Restaurar" : "Reset"}
            </button>
          )}
        </div>
      </div>

      <div className="mt-2 flex gap-1">
        {!illustration ? (
          <Button
            data-testid={`comic-illu-gen-${comic.slug}-${index}`}
            onClick={() => onGenerate(index, 0, prompt, false)}
            disabled={busy}
            className="brutal-btn bg-canales-blue text-white px-2 py-1 text-xs flex-1"
          >
            <Wand2 className="w-3 h-3 mr-1" /> {lang === "es" ? "Generar hero" : "Generate hero"}
          </Button>
        ) : (
          <Button
            data-testid={`comic-illu-regen-${comic.slug}-${index}`}
            onClick={() => onRegenerate(index, 0, prompt)}
            disabled={busy}
            className="brutal-btn bg-canales-yellow text-black px-2 py-1 text-xs flex-1"
          >
            <RotateCcw className="w-3 h-3 mr-1" /> {lang === "es" ? "Regenerar" : "Regenerate"}
          </Button>
        )}
      </div>

      {/* Scenes (multi-panel page) */}
      {scenes.length > 0 && (
        <div className="mt-3 border-t-2 border-black pt-2 space-y-2">
          <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-blue">
            {lang === "es" ? "Escenas por página" : "Page scenes"}
          </div>
          {scenes.map((sc, si) => (
            <SceneRow
              key={si}
              comic={comic}
              pageIndex={index}
              sceneIndex={si + 1}
              scene={sc}
              lang={lang}
              busy={busy}
              onGenerate={onGenerate}
              onRegenerate={onRegenerate}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const SceneRow = ({ comic, pageIndex, sceneIndex, scene, lang, busy, onGenerate, onRegenerate }) => {
  const [prompt, setPrompt] = useState(scene.prompt || "");
  const [editing, setEditing] = useState(false);
  return (
    <div className="brutal-sm p-2 bg-canales-cream" data-testid={`comic-scene-row-${comic.slug}-${pageIndex}-${sceneIndex}`}>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-display font-black uppercase tracking-widest text-canales-red">
          Escena {sceneIndex}
        </div>
        <button
          onClick={() => setEditing((e) => !e)}
          className="text-[10px] underline font-display font-bold"
        >
          {editing ? "Ocultar" : "Editar"}
        </button>
      </div>
      {editing ? (
        <textarea
          rows={3}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="w-full brutal-sm bg-white p-1.5 text-[10px] font-body"
          data-testid={`comic-scene-prompt-${comic.slug}-${pageIndex}-${sceneIndex}`}
        />
      ) : (
        <div className="text-[10px] font-body text-neutral-600 line-clamp-2">{prompt}</div>
      )}
      <div className="mt-1 flex gap-1">
        <Button
          onClick={() => onGenerate(pageIndex, sceneIndex, prompt, false)}
          disabled={busy}
          data-testid={`comic-scene-gen-${comic.slug}-${pageIndex}-${sceneIndex}`}
          className="brutal-btn bg-canales-blue text-white px-2 py-0.5 text-[10px] flex-1"
        >
          <Wand2 className="w-3 h-3 mr-1" /> Generar
        </Button>
        <Button
          onClick={() => onRegenerate(pageIndex, sceneIndex, prompt)}
          disabled={busy}
          className="brutal-btn bg-canales-yellow text-black px-2 py-0.5 text-[10px]"
        >
          <RotateCcw className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
};

export default function ComicIllustrations() {
  const { lang } = useLang();
  const [selected, setSelected] = useState(COMICS[0].slug);
  const [map, setMap] = useState({}); // key: page_index -> illustration
  const [busy, setBusy] = useState({}); // key: page_index -> bool
  const [bulkBusy, setBulkBusy] = useState(false);

  const comic = useMemo(() => COMICS.find((c) => c.slug === selected), [selected]);

  const refresh = async () => {
    if (!comic) return;
    try {
      const { data } = await api.get(`/comics/${comic.slug}/illustrations`);
      const m = {};
      for (const it of data) {
        const p = it.page_index || 0;
        const s = it.scene_index || 0;
        if (!m[p]) m[p] = {};
        m[p][s] = it;
      }
      setMap(m);
    } catch {
      setMap({});
    }
  };

  // Refresh illustrations map when the selected comic changes.
  useEffect(() => { refresh(); }, [selected]);

  const generateOne = async (pageIndex, prompt, force = false) => {
    setBusy((b) => ({ ...b, [pageIndex]: true }));
    try {
      await api.post("/admin/comics/illustration", {
        slug: comic.slug,
        page_index: pageIndex,
        prompt,
        force,
      });
      await refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setBusy((b) => ({ ...b, [pageIndex]: false }));
    }
  };

  const regenerateOne = (pageIndex, prompt) => generateOne(pageIndex, prompt, true);

  const generateAllMissing = async () => {
    if (!comic) return;
    setBulkBusy(true);
    try {
      for (let i = 0; i < comic.pages.length; i++) {
        if (map[i]) continue;
        const prompt = buildPagePrompt(comic, comic.pages[i], i);
        await generateOne(i, prompt, false);
      }
      toast.success(lang === "es" ? "Todas las páginas listas" : "All pages ready");
    } finally {
      setBulkBusy(false);
    }
  };

  const regenerateAllScenesFor = async (comicToUse) => {
    let done = 0, total = 0;
    // Compute total scene count using deriveScenes so PADDED/derived scenes
    // also count — otherwise pages with few explicit scenes leave empty tiles.
    const perPageScenes = comicToUse.pages.map((p, i) => {
      if (p.layout === "cover") return [];
      const explicit = Array.isArray(p.scenes) ? p.scenes : [];
      if (explicit.length >= 6) return explicit;
      const derived = deriveScenes(comicToUse, p, i);
      return derived.length > explicit.length ? derived : explicit;
    });
    for (const arr of perPageScenes) total += 1 + arr.length; // hero + scenes
    toast(lang === "es"
      ? `[${comicToUse.title.es}] Regenerando ${total} imágenes…`
      : `[${comicToUse.title.en}] Regenerating ${total} images…`);
    for (let i = 0; i < comicToUse.pages.length; i++) {
      const p = comicToUse.pages[i];
      const heroPrompt = buildPagePrompt(comicToUse, p, i);
      try {
        await api.post("/admin/comics/illustration", {
          slug: comicToUse.slug, page_index: i, scene_index: 0, prompt: heroPrompt, force: true,
        });
      } catch (e) { console.error("scene 0 err", e); }
      done += 1;
      const scenes = perPageScenes[i];
      for (let s = 0; s < scenes.length; s++) {
        const scenePrompt = scenes[s].prompt || heroPrompt;
        try {
          await api.post("/admin/comics/illustration", {
            slug: comicToUse.slug, page_index: i, scene_index: s + 1, prompt: scenePrompt, force: true,
          });
        } catch (e) { console.error(`scene ${s+1} err`, e); }
        done += 1;
      }
      if (comicToUse.slug === comic?.slug) await refresh();
    }
    return { done, total };
  };

  const regenerateAllScenes = async () => {
    if (!comic) return;
    if (!window.confirm(
      lang === "es"
        ? `¿Regenerar TODAS las escenas de "${comic.title.es}" con el nuevo prompt (uniforme oficial + esquina vacía)? Puede tardar varios minutos y consume créditos.`
        : `Regenerate ALL scenes of "${comic.title.en}" with the new prompt (official uniform + corner-empty)? Takes several minutes and uses credits.`
    )) return;
    setBulkBusy(true);
    try {
      const r = await regenerateAllScenesFor(comic);
      toast.success(
        lang === "es"
          ? `Listo — ${r.done}/${r.total} escenas regeneradas`
          : `Done — ${r.done}/${r.total} scenes regenerated`
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const regenerateEveryComic = async () => {
    if (!window.confirm(
      lang === "es"
        ? `¿Regenerar TODOS los ${COMICS.length} cómics de golpe con el uniforme oficial + nuevo estilo? Tomará 15-30 minutos y consume créditos considerables. Puedes seguir usando la app mientras corre.`
        : `Regenerate ALL ${COMICS.length} comics at once with the official uniform + new style? Takes 15-30 minutes and uses significant credits. You can keep using the app while it runs.`
    )) return;
    setBulkBusy(true);
    let grandDone = 0, grandTotal = 0;
    try {
      for (let idx = 0; idx < COMICS.length; idx++) {
        const c = COMICS[idx];
        toast(lang === "es"
          ? `Cómic ${idx + 1}/${COMICS.length}: ${c.title.es}`
          : `Comic ${idx + 1}/${COMICS.length}: ${c.title.en}`);
        const r = await regenerateAllScenesFor(c);
        grandDone += r.done;
        grandTotal += r.total;
      }
      toast.success(
        lang === "es"
          ? `¡Listo! ${grandDone}/${grandTotal} escenas regeneradas en los ${COMICS.length} cómics.`
          : `Done! ${grandDone}/${grandTotal} scenes regenerated across all ${COMICS.length} comics.`
      );
    } finally {
      setBulkBusy(false);
    }
  };

  const generateFirst3 = async () => {
    if (!comic) return;
    setBulkBusy(true);
    try {
      const limit = Math.min(3, comic.pages.length);
      for (let i = 0; i < limit; i++) {
        if (map[i]) continue;
        const prompt = buildPagePrompt(comic, comic.pages[i], i);
        await generateOne(i, prompt, false);
      }
      toast.success(lang === "es" ? "Primeras 3 páginas listas" : "First 3 pages ready");
    } finally {
      setBulkBusy(false);
    }
  };

  const deleteAll = async () => {
    if (!comic) return;
    if (!window.confirm(lang === "es" ? `¿Borrar todas las ilustraciones de "${comic.title.es}"?` : `Delete all illustrations for "${comic.title.en}"?`)) return;
    await api.delete(`/admin/comics/${comic.slug}/illustrations`);
    toast.success(lang === "es" ? "Ilustraciones borradas" : "Illustrations deleted");
    refresh();
  };

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="admin-comic-illustrations">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-black text-4xl flex items-center gap-3">
            <BookOpen className="w-8 h-8 text-canales-red" />
            {lang === "es" ? "Ilustraciones de Cómics AI" : "AI Comic Illustrations"}
          </h1>
          <p className="font-body text-neutral-700">
            {lang === "es"
              ? "Genera arte 4K con Nano Banana para cada página del cómic. La imagen aparece automáticamente en el reader."
              : "Generate 4K Nano Banana art for each comic page. Images auto-appear in the reader."}
          </p>
        </div>
      </div>

      {/* Comic selector */}
      <div className="flex flex-wrap gap-2" data-testid="comic-selector">
        {COMICS.map((c) => (
          <button
            key={c.slug}
            onClick={() => setSelected(c.slug)}
            data-testid={`comic-tab-${c.slug}`}
            className={`brutal-btn px-3 py-1.5 text-sm font-display font-bold ${
              selected === c.slug ? "bg-canales-red text-white" : "bg-white text-black"
            }`}
          >
            {c.title[lang]}
          </button>
        ))}
      </div>

      {/* Bulk actions */}
      {comic && (
        <div className="brutal p-4 bg-canales-cream flex items-center justify-between flex-wrap gap-3">
          <div className="font-body text-sm">
            <span className="font-display font-black">{comic.title[lang]}</span>
            <span className="ml-2 text-neutral-600">
              {Object.keys(map).length} / {comic.pages.length}{" "}
              {lang === "es" ? "páginas generadas" : "pages generated"}
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={generateFirst3}
              disabled={bulkBusy}
              data-testid="comic-illu-generate-first-3"
              className="brutal-btn bg-canales-yellow text-black px-4 py-2"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {lang === "es" ? "Primeras 3 páginas" : "First 3 pages"}
            </Button>
            <Button
              onClick={generateAllMissing}
              disabled={bulkBusy}
              data-testid="comic-illu-generate-all"
              className="brutal-btn bg-canales-red text-white px-4 py-2"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {lang === "es" ? "Generar todas" : "Generate all"}
            </Button>
            <Button
              onClick={regenerateAllScenes}
              disabled={bulkBusy}
              data-testid="comic-illu-regen-all-scenes"
              className="brutal-btn bg-black text-white px-4 py-2"
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-2" />}
              {lang === "es" ? "Regenerar TODAS (nuevo prompt)" : "Regenerate ALL (new prompt)"}
            </Button>
            <Button
              onClick={regenerateEveryComic}
              disabled={bulkBusy}
              data-testid="comic-illu-regen-every-comic"
              className="brutal-btn text-white px-4 py-2"
              style={{ background: "linear-gradient(180deg,#0033A0,#0d1a3f)" }}
            >
              {bulkBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {lang === "es" ? `Regenerar TODOS los ${COMICS.length} cómics ☕` : `Regenerate ALL ${COMICS.length} comics ☕`}
            </Button>
            <Button
              onClick={deleteAll}
              data-testid="comic-illu-delete-all"
              className="brutal-btn bg-white text-black px-3 py-2 text-sm"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              {lang === "es" ? "Borrar todo" : "Delete all"}
            </Button>
          </div>
        </div>
      )}

      {/* Grid of pages */}
      {comic && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {comic.pages.map((p, i) => (
            <PageCell
              key={i}
              comic={comic}
              page={p}
              index={i}
              illustration={map[i]?.[0]}
              lang={lang}
              busy={!!busy[i]}
              onGenerate={generateOne}
              onRegenerate={regenerateOne}
            />
          ))}
        </div>
      )}

      <div className="text-xs font-body text-neutral-500">
        {lang === "es"
          ? "Nota: cada imagen tarda ~15-25 s. Puedes regenerar cualquiera para variar el resultado."
          : "Note: each image takes ~15-25s. You can regenerate any one for a fresh result."}
      </div>
    </motion.div>
  );
}
